import React, { useState } from "react";
import { useModalProtection } from "@/hooks/useModalProtection";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from '@tanstack/react-query';
import { invalidateAlertes } from '@/lib/queryInvalidation';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Plus, FileText, Loader2, CheckCircle2, Clock, Send, XCircle, Edit, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ClaudeVisionImport } from "@/components/ocr";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { sanitizeUrl } from "@/lib/security/urlSanitizer";

// Schema de validation Zod pour les devis
const devisSchema = z.object({
  statut: z.enum(["brouillon", "envoye", "accepte", "refuse"], {
    errorMap: () => ({ message: "Sélectionnez un statut valide" }),
  }),
  montant_ht: z.number().positive("Le montant HT doit être supérieur à 0"),
  tva: z.number().nonnegative("La TVA ne peut pas être négative"),
  montant_ttc: z.number().nonnegative("Le montant TTC ne peut pas être négatif"),
});

interface QuoteManagerProps {
  chantierId: string;
  devis: any[];
  onUpdate: () => void;
  isHighlighted?: boolean; // Pour l'effet de highlight depuis la checklist
}

const QuoteManager = ({ chantierId, devis = [], onUpdate, isHighlighted = false }: QuoteManagerProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { entrepriseId } = useSubscription();
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDeletingDevis, setIsDeletingDevis] = useState(false);
  const [isTogglingActif, setIsTogglingActif] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [editingDevis, setEditingDevis] = useState<any>(null);
  const [formData, setFormData] = useState({
    montant_ht: '',
    tva: '20',
    montant_ttc: 0,
    statut: 'accepte',
  });

  // Protection contre les refetch Realtime quand un dialog de saisie est ouvert
  useModalProtection(open);
  useModalProtection(editOpen);

  // État pour le guidage onboarding (clignotement des éléments)
  // Flow OCR: Statut → Choisir fichier → Enregistrer (dans ClaudeVisionImport)
  // Flow manuel: Statut → Montant HT → Créer le devis
  const [ocrDataExtracted, setOcrDataExtracted] = useState(false);
  // État pour arrêter le clignotement dès que l'utilisateur clique sur le select statut
  const [statutClicked, setStatutClicked] = useState(false);

  // Calculer quelle étape du guidage on est
  const isStatutSelected = formData.statut !== '';
  const isMontantHTFilled = parseFloat(formData.montant_ht.toString().replace(',', '.')) > 0;

  // Highlight statut tant qu'il n'est pas sélectionné ET que l'utilisateur n'a pas encore cliqué
  const shouldHighlightStatut = isHighlighted && open && !isStatutSelected && !statutClicked;
  // Highlight "Choisir fichier" si statut OK et pas encore de données OCR
  const shouldHighlightFileUpload = isHighlighted && open && isStatutSelected && !ocrDataExtracted;
  // Highlight "Enregistrer" (OCR) si données extraites
  const shouldHighlightSaveButton = isHighlighted && open && isStatutSelected && ocrDataExtracted;
  // Highlight "Créer le devis" (manuel) si statut OK, montant HT rempli, et PAS d'OCR en cours
  const shouldHighlightCreateButton = isHighlighted && open && isStatutSelected && isMontantHTFilled && !ocrDataExtracted;


  const devisList = Array.isArray(devis) ? devis : [];
  const devisActif = devisList.find(d => d.actif);

  // Calculs pour le récapitulatif financier
  const totalHT = devisActif?.montant_ht || 0;
  const totalTVA = devisActif ? (devisActif.montant_ht * devisActif.tva / 100) : 0;
  const totalTTC = devisActif?.montant_ttc || 0;

  const calculateTTC = (ht: number, tva: number) => {
    return ht * (1 + tva / 100);
  };

  const handleHTChange = (value: string) => {
    const ht = parseFloat(value) || 0;
    const tva = parseFloat(formData.tva) || 0;
    const ttc = calculateTTC(ht, tva);
    setFormData({ ...formData, montant_ht: value, montant_ttc: ttc });
  };

  const handleTVAChange = (value: string) => {
    const tva = parseFloat(value) || 0;
    const ht = parseFloat(formData.montant_ht) || 0;
    const ttc = calculateTTC(ht, tva);
    setFormData({ ...formData, tva: value, montant_ttc: ttc });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Protection double-clic
    if (loading) return;

    // Validation critique : vérifier que chantierId existe
    if (!chantierId) {
      toast({
        title: "Erreur",
        description: "ID du chantier manquant. Impossible de créer le devis.",
        variant: "destructive",
      });
      return;
    }

    // Normalisation des montants : remplacer virgule par point pour format français
    const montantHtStr = formData.montant_ht.toString().replace(',', '.');
    const tvaStr = formData.tva.toString().replace(',', '.');

    const montant_ht = parseFloat(montantHtStr) || 0;
    const tva = parseFloat(tvaStr) || 0;

    // Validation avec Zod
    const validationData = {
      statut: formData.statut,
      montant_ht,
      tva,
      montant_ttc: formData.montant_ttc,
    };

    const result = devisSchema.safeParse(validationData);
    if (!result.success) {
      const firstError = result.error.errors[0];
      toast({
        title: "Données invalides",
        description: firstError.message,
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      if (import.meta.env.DEV) {
        console.log('[QuoteManager] Début création devis pour chantier:', chantierId);
        console.log('[QuoteManager] Montants normalisés:', { montant_ht, tva, montant_ttc: formData.montant_ttc });
      }

      const fichier_url = null; // Fichier désactivé pour les devis

      // Auto-incrémenter la version
      const maxVersion = devisList.length > 0 
        ? Math.max(...devisList.map(d => parseInt(d.version?.replace('V', '') || '0'))) 
        : 0;
      const newVersion = `V${maxVersion + 1}`;

      const isFirstDevis = devisList.length === 0;

      // Désactiver tous les autres devis si c'est le premier
      if (isFirstDevis) {
        if (import.meta.env.DEV) console.log('[QuoteManager] Premier devis, désactivation des anciens devis');
        await supabase
          .from("devis")
          .update({ actif: false })
          .eq("chantier_id", chantierId);
      }

      const devisData = {
        montant_ht,
        tva,
        montant_ttc: formData.montant_ttc,
        statut: formData.statut,
        chantier_id: chantierId,
        fichier_url,
        version: newVersion,
        actif: isFirstDevis, // Premier devis = actif
      };

      if (import.meta.env.DEV) console.log('[QuoteManager] DevisData envoyé à la base:', devisData);

      const { error } = await supabase
        .from("devis")
        .insert(devisData);
      
      if (error) {
        console.error('[QuoteManager] Erreur insertion devis:', error);
        throw error;
      }

      if (import.meta.env.DEV) console.log('[QuoteManager] Devis créé avec succès');

      toast({
        title: "Succès",
        description: `Devis ${newVersion} créé avec succès`,
      });

      setOpen(false);
      setFormData({
        montant_ht: '',
        tva: '20',
        montant_ttc: 0,
        statut: 'accepte',
      });
      setFile(null);
      setOcrDataExtracted(false);
      onUpdate();
    } catch (error: any) {
      console.error('[QuoteManager] Erreur création devis:', error);
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActif = async (devisId: string) => {
    setIsTogglingActif(true);
    try {
      // Désactiver tous les autres devis
      await supabase
        .from("devis")
        .update({ actif: false })
        .eq("chantier_id", chantierId);

      // Activer le devis sélectionné
      const { error } = await supabase
        .from("devis")
        .update({ actif: true })
        .eq("id", devisId);

      if (error) throw error;

      toast({
        title: "Succès",
        description: "Devis actif mis à jour",
      });

      onUpdate();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsTogglingActif(false);
    }
  };

  const handleOpenEdit = (d: any) => {
    setEditingDevis(d);
    setFormData({
      montant_ht: d.montant_ht.toString(),
      tva: d.tva.toString(),
      montant_ttc: d.montant_ttc,
      statut: d.statut,
    });
    setEditOpen(true);
  };

  const handleUpdateDevis = async (e: React.FormEvent) => {
    e.preventDefault();

    // Protection double-clic
    if (loading) return;

    // Normalisation des montants : remplacer virgule par point pour format français
    const montantHtStr = formData.montant_ht.toString().replace(',', '.');
    const tvaStr = formData.tva.toString().replace(',', '.');

    const montant_ht = parseFloat(montantHtStr) || 0;
    const tva = parseFloat(tvaStr) || 0;

    // Validation avec Zod
    const validationData = {
      statut: formData.statut,
      montant_ht,
      tva,
      montant_ttc: formData.montant_ttc,
    };

    const result = devisSchema.safeParse(validationData);
    if (!result.success) {
      const firstError = result.error.errors[0];
      toast({
        title: "Données invalides",
        description: firstError.message,
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      if (import.meta.env.DEV) {
        console.log('[QuoteManager] Début modification devis:', editingDevis?.id);
        console.log('[QuoteManager] Montants normalisés pour update:', { montant_ht, tva, montant_ttc: formData.montant_ttc });
      }

      const { error } = await supabase
        .from("devis")
        .update({
          montant_ht,
          tva,
          montant_ttc: formData.montant_ttc,
          statut: formData.statut,
        })
        .eq("id", editingDevis.id);

      if (error) {
        console.error('[QuoteManager] Erreur modification devis:', error);
        throw error;
      }

      if (import.meta.env.DEV) console.log('[QuoteManager] Devis modifié avec succès');

      // Invalider les alertes React Query (remplace l'ancien event devis-updated)
      if (entrepriseId) invalidateAlertes(queryClient, entrepriseId);

      toast({
        title: "Succès",
        description: "Devis modifié avec succès",
      });

      setEditOpen(false);
      setEditingDevis(null);
      onUpdate();
    } catch (error: any) {
      console.error('[QuoteManager] Erreur modification devis:', error);
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDevis = async (devisId: string) => {
    setIsDeletingDevis(true);
    try {
      const { error } = await supabase
        .from("devis")
        .delete()
        .eq("id", devisId);

      if (error) throw error;

      toast({
        title: "Succès",
        description: "Devis supprimé avec succès",
      });

      onUpdate();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsDeletingDevis(false);
    }
  };

  const getStatutBadge = (statut: string) => {
    const config = {
      brouillon: { label: "Brouillon", variant: "secondary" as const, icon: Clock },
      envoye: { label: "Envoyé", variant: "default" as const, icon: Send },
      accepte: { label: "Accepté", variant: "default" as const, icon: CheckCircle2 },
      refuse: { label: "Refusé", variant: "destructive" as const, icon: XCircle },
    };
    const { label, variant, icon: Icon } = config[statut as keyof typeof config] || config.brouillon;
    return (
      <Badge variant={variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {label}
      </Badge>
    );
  };

  return (
    <>
      {/* Récapitulatif Financier - affiché uniquement s'il y a un devis actif */}
      {devisActif && (
        <Card className="mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">📄 Récapitulatif Devis Actif</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-6">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground font-medium">Nombre de devis</p>
                <p className="text-3xl font-black text-foreground">{devisList.length}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground font-medium">Montant HT</p>
                <p className="text-3xl font-black text-foreground">{totalHT.toFixed(2)} €</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground font-medium">TVA ({devisActif.tva}%)</p>
                <p className="text-3xl font-black text-amber-600">{totalTVA.toFixed(2)} €</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground font-medium">Montant TTC</p>
                <p className="text-3xl font-black text-primary">{totalTTC.toFixed(2)} €</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Devis</CardTitle>
              <CardDescription>
                Gestion des devis du chantier
              </CardDescription>
            </div>
          <Dialog open={open} onOpenChange={(isOpen) => {
            // Bloquer la fermeture si l'OCR a des données (multi-scan ou mono)
            if (!isOpen && ocrDataExtracted) {
              return;
            }
            setOpen(isOpen);
            if (!isOpen) {
              // Reset les états de guidage quand on ferme la modal
              setOcrDataExtracted(false);
              setStatutClicked(false);
              setFormData({ montant_ht: '', tva: '20', montant_ttc: 0, statut: '' });
            }
          }}>
            <DialogTrigger asChild>
              <Button className={cn(isHighlighted && devisList.length === 0 && "animate-highlight-pulse")}>
                <Plus className="mr-2 h-4 w-4" />
                Nouveau devis
              </Button>
            </DialogTrigger>
            <DialogContent variant="industrial" className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Créer un nouveau devis</DialogTitle>
                <DialogDescription>
                  Choisissez une méthode d'import
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {/* Statut du devis - PARTAGÉ entre les deux onglets */}
                <div className="space-y-2">
                  <Label htmlFor="statut" className="flex items-center gap-1">
                    Statut du devis
                    <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={formData.statut}
                    onValueChange={(value) => setFormData({ ...formData, statut: value })}
                    onOpenChange={(isOpen) => { if (isOpen) setStatutClicked(true); }}
                    required
                  >
                    <SelectTrigger className={cn(shouldHighlightStatut && "animate-subtle-glow")}>
                      <SelectValue placeholder="Sélectionnez un statut" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="brouillon">Brouillon</SelectItem>
                      <SelectItem value="envoye">Envoyé</SelectItem>
                      <SelectItem value="accepte">Accepté</SelectItem>
                      <SelectItem value="refuse">Refusé</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">Brouillon : en cours de rédaction. Envoyé : transmis au client. Accepté : validé, sert de base au budget.</p>
                </div>

                {/* Onglets Import OCR / Saisie manuelle */}
                <Tabs defaultValue="ocr">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="ocr">Scan IA</TabsTrigger>
                    <TabsTrigger value="manual">Saisie manuelle</TabsTrigger>
                  </TabsList>

                  <TabsContent value="ocr">
                    <ClaudeVisionImport
                      documentType="devis"
                      chantierId={chantierId}
                      statut={formData.statut}
                      onSuccess={() => {
                        setOpen(false);
                        setOcrDataExtracted(false);
                        onUpdate();
                      }}
                      onCancel={() => {
                        setOcrDataExtracted(false);
                        setOpen(false);
                      }}
                      onDataExtracted={() => setOcrDataExtracted(true)}
                      highlightFileUpload={shouldHighlightFileUpload}
                      highlightSaveButton={shouldHighlightSaveButton}
                    />
                  </TabsContent>

                  <TabsContent value="manual">
                    <form onSubmit={handleSubmit}>
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="montant_ht">Montant HT (€)</Label>
                            <Input
                              id="montant_ht"
                              type="number"
                              step="0.01"
                              value={formData.montant_ht}
                              onChange={(e) => handleHTChange(e.target.value)}
                              required
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="tva">TVA (%)</Label>
                            <Input
                              id="tva"
                              type="number"
                              step="0.01"
                              value={formData.tva}
                              onChange={(e) => handleTVAChange(e.target.value)}
                              required
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="montant_ttc">Montant TTC (€)</Label>
                          <Input
                            id="montant_ttc"
                            type="number"
                            step="0.01"
                            value={formData.montant_ttc.toFixed(2)}
                            readOnly
                            className="bg-muted"
                          />
                        </div>

                        <DialogFooter className="mt-6">
                          <Button
                            type="submit"
                            disabled={loading}
                            className={cn("bg-gradient-to-r from-[#F7B500] to-[#FF9500] text-black font-bold hover:brightness-110 shadow-[0_6px_20px_rgba(247,181,0,0.2)]", shouldHighlightCreateButton && "animate-highlight-pulse")}
                          >
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Créer le devis
                          </Button>
                        </DialogFooter>
                      </div>
                    </form>
                  </TabsContent>
                </Tabs>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>

      <CardContent>
        {devisList.length > 0 ? (
          <div className="space-y-4">
            {/* Tableau des devis */}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Version</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Montant HT</TableHead>
                  <TableHead>TVA</TableHead>
                  <TableHead>Montant TTC</TableHead>
                  <TableHead>Créé le</TableHead>
                  <TableHead>Actif</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {devisList.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell className="font-medium">{d.version}</TableCell>
                    <TableCell>{getStatutBadge(d.statut)}</TableCell>
                    <TableCell>{d.montant_ht?.toFixed(2)} €</TableCell>
                    <TableCell>{d.tva}%</TableCell>
                    <TableCell className="font-bold">{d.montant_ttc?.toFixed(2)} €</TableCell>
                    <TableCell>{new Date(d.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Button
                        variant={d.actif ? "default" : "outline"}
                        size="sm"
                        onClick={() => !d.actif && handleToggleActif(d.id)}
                        disabled={d.actif || isTogglingActif}
                      >
                        {isTogglingActif && !d.actif ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : d.actif ? (
                          <CheckCircle2 className="h-4 w-4" />
                        ) : (
                          "Activer"
                        )}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {sanitizeUrl(d.fichier_url) && (
                          <Button variant="ghost" size="sm" asChild>
                            <a href={sanitizeUrl(d.fichier_url)!} target="_blank" rel="noopener noreferrer">
                              <FileText className="h-4 w-4" />
                            </a>
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleOpenEdit(d)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
                              <AlertDialogDescription>
                                Êtes-vous sûr de vouloir supprimer le devis {d.version} ? Cette action est irréversible.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel disabled={isDeletingDevis} className="text-white/30 bg-transparent hover:text-white/55 hover:bg-transparent">Annuler</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteDevis(d.id)}
                                disabled={isDeletingDevis}
                                className="bg-[rgba(230,57,70,0.1)] border border-[rgba(230,57,70,0.25)] text-[#E63946] hover:bg-[rgba(230,57,70,0.15)]"
                              >
                                {isDeletingDevis ? (
                                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Suppression...</>
                                ) : (
                                  "Supprimer"
                                )}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-12">
            <FileText className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg font-semibold text-muted-foreground mb-2">
              Aucun devis enregistré
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              Créez votre premier devis pour commencer à suivre la rentabilité du chantier
            </p>
          </div>
        )}
      </CardContent>

      {/* Dialog d'édition */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent variant="industrial">
          <DialogHeader>
            <DialogTitle>Modifier le devis {editingDevis?.version}</DialogTitle>
            <DialogDescription>
              Modifiez les montants et le statut du devis
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateDevis}>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit_statut">Statut</Label>
                <Select 
                  value={formData.statut} 
                  onValueChange={(value) => setFormData({ ...formData, statut: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="brouillon">Brouillon</SelectItem>
                    <SelectItem value="envoye">Envoyé</SelectItem>
                    <SelectItem value="accepte">Accepté</SelectItem>
                    <SelectItem value="refuse">Refusé</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_montant_ht">Montant HT (€)</Label>
                  <Input
                    id="edit_montant_ht"
                    type="number"
                    step="0.01"
                    value={formData.montant_ht}
                    onChange={(e) => handleHTChange(e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_tva">TVA (%)</Label>
                  <Input
                    id="edit_tva"
                    type="number"
                    step="0.01"
                    value={formData.tva}
                    onChange={(e) => handleTVAChange(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_montant_ttc">Montant TTC (€)</Label>
                <Input
                  id="edit_montant_ttc"
                  type="number"
                  step="0.01"
                  value={formData.montant_ttc.toFixed(2)}
                  readOnly
                  className="bg-muted"
                />
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="submit" disabled={loading} className="bg-gradient-to-r from-[#F7B500] to-[#FF9500] text-black font-bold hover:brightness-110 shadow-[0_6px_20px_rgba(247,181,0,0.2)]">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Enregistrer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
    </>
  );
};

export default QuoteManager;
